import React, { ReactNode, useCallback, useMemo, useState } from "react";
import { connect } from "react-redux";

import Radio from "antd/es/radio";
import Space from "antd/es/space";
import { useIntl } from "react-intl";

import Team from "@mapmycustomers/shared/types/Team";
import User from "@mapmycustomers/shared/types/User";
import { SelectField } from "@mapmycustomers/ui";

import RadioGroup from "@app/component/input/RadioGroup";
import itemToValueLabel from "@app/component/input/utils/itemToValueLabel";
import UserList from "@app/component/UsersSelect/components/UserList";
import { getTeams, getUsers } from "@app/store/members";
import { RootState } from "@app/store/rootReducer";
import ActiveCheckInsAccessValue from "@app/types/ActiveCheckInsAccessValue";

import styles from "./ActiveCheckInAccess.module.scss";
import commonStyles from "./FormItems.module.scss";

interface ActiveCheckInAccessProps {
  onChange?: (value: ActiveCheckInsAccessValue) => void;
  value?: ActiveCheckInsAccessValue;
}

interface Props extends ActiveCheckInAccessProps {
  teams: Team[];
  users: User[];
}

const ActiveCheckInAccess: React.FC<Props> = ({ onChange, teams, users, value }) => {
  const intl = useIntl();

  const [radioValue, setRadioValue] = useState<keyof ActiveCheckInsAccessValue>(
    !value || value.allUsers ? "allUsers" : value.teamIds?.length ? "teamIds" : "userIds"
  );

  const handleTypeChange = useCallback(
    (type: keyof ActiveCheckInsAccessValue) => {
      setRadioValue(type);
      if (type === "allUsers") {
        onChange?.({ allUsers: true, teamIds: [], userIds: [] });
      } else {
        onChange?.({ allUsers: false, teamIds: [], userIds: [] });
      }
    },
    [onChange]
  );

  const teamOptions = useMemo(() => teams.map(itemToValueLabel), [teams]);
  const handleTeamIdsChange = useCallback(
    (teamIds: Team["id"][]) => {
      onChange?.({ allUsers: false, teamIds, userIds: [] });
    },
    [onChange]
  );

  const selectedUserIdsSet = useMemo(() => new Set(value?.userIds ?? []), [value?.userIds]);
  const handleUserClick = useCallback(
    (userId: User["id"]) => {
      if (selectedUserIdsSet.has(userId)) {
        onChange?.({
          allUsers: false,
          teamIds: [],
          userIds: Array.from(selectedUserIdsSet).filter((id) => id !== userId),
        });
      } else {
        onChange?.({ allUsers: false, teamIds: [], userIds: [...selectedUserIdsSet, userId] });
      }
    },
    [onChange, selectedUserIdsSet]
  );

  return (
    <div>
      <div>
        {intl.formatMessage({
          id: "settings.organizationDetails.activities.activeCheckInAccess.switch.title",
          defaultMessage: "Enable for:",
          description:
            "Title of the access type switch for the Two-Step check-in setting in org settings",
        })}
      </div>
      <RadioGroup<keyof ActiveCheckInsAccessValue> onChange={handleTypeChange} value={radioValue}>
        <Space className={commonStyles.space} direction="vertical">
          <Radio className={commonStyles.radio} value="userIds">
            {intl.formatMessage(
              {
                id: "settings.organizationDetails.activities.activeCheckInAccess.switch.users",
                defaultMessage:
                  "Only specific <b>users</b>. All unselected users will have one-step check-ins.",
                description: "Users option in the Two-Step check-in setting in org settings",
              },
              {
                b: (text: ReactNode[]) => <strong>{text}</strong>,
              }
            )}
          </Radio>
          <Radio className={commonStyles.radio} value="teamIds">
            {intl.formatMessage(
              {
                id: "settings.organizationDetails.activities.activeCheckInAccess.switch.teams",
                defaultMessage:
                  "Only specific <b>teams</b>. All other teams will have one-step check-ins.",
                description: "Teams option in the Two-Step check-in setting in org settings",
              },
              {
                b: (text: ReactNode[]) => <strong>{text}</strong>,
              }
            )}
          </Radio>
          <Radio className={commonStyles.radio} value="allUsers">
            {intl.formatMessage({
              id: "settings.organizationDetails.activities.activeCheckInAccess.switch.allUsers",
              defaultMessage: "All users",
              description: "All Users option in the Two-Step check-in setting in org settings",
            })}
          </Radio>
        </Space>
      </RadioGroup>
      {radioValue === "teamIds" && (
        <SelectField<Team["id"][]>
          className={styles.selectField}
          dropdownMatchSelectWidth={false}
          label={intl.formatMessage({
            id: "settings.organizationDetails.activities.activeCheckInAccess.teamsField",
            defaultMessage: "Select Teams",
            description: "Teams field in the Two-Step check-in setting in org settings",
          })}
          mode="multiple"
          onChange={handleTeamIdsChange}
          options={teamOptions}
          showSearch
          value={value?.teamIds}
        />
      )}
      {radioValue === "userIds" && (
        <div className={styles.users}>
          <div className={styles.userListHeader}>
            <div>
              {intl.formatMessage({
                id: "settings.organizationDetails.activities.activeCheckInAccess.usersField",
                defaultMessage: "Select Users",
                description: "Users field in the Two-Step check-in setting in org settings",
              })}
            </div>
            <div className={styles.stats}>
              {intl.formatMessage(
                {
                  id: "settings.organizationDetails.activities.activeCheckInAccess.usersField.selected",
                  defaultMessage: "{count} / {total} selected",
                  description:
                    "User field's stats in the Two-Step check-in setting in org settings",
                },
                {
                  count: value?.userIds?.length ?? 0,
                  total: users.length,
                }
              )}
            </div>
          </div>
          <UserList
            className={styles.userList}
            onUserClick={handleUserClick}
            selectedUserIdsSet={selectedUserIdsSet}
            showOnlySelectedSwitch={false}
            users={users}
          />
        </div>
      )}
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  teams: getTeams(state),
  users: getUsers(state),
});

export default connect(mapStateToProps)(ActiveCheckInAccess);
